import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useForm, useWatch } from "react-hook-form";
import { BillingPlanStatus } from "@api/shared-runtime/enums";
import { useTRPC } from "@/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { formatPlanDate, type BillingPlanData } from "./billing-plan-values";

type EndPlanValues = { endDate: string };

export function EndBillingPlanDialog({
  data,
  onSaved,
  onBusyChange,
  onReload,
  busy,
}: {
  data: BillingPlanData;
  onSaved: () => Promise<void>;
  onBusyChange: (busy: boolean) => void;
  onReload: () => void;
  busy: boolean;
}) {
  const trpc = useTRPC();
  const [open, setOpen] = useState(false);
  const form = useForm<EndPlanValues>({
    defaultValues: { endDate: data.defaultEndDate },
  });
  const endDate = useWatch({ control: form.control, name: "endDate" });
  const currentPlan = data.plans.find(
    (plan) => plan.status === BillingPlanStatus.Active,
  );
  const hasScheduledEnd = Boolean(currentPlan?.effectiveEndsAt);
  const mutation = useMutation(
    trpc.adminBillingPlans.endPlan.mutationOptions({ onSuccess: onSaved }),
  );

  async function endPlan(values: EndPlanValues) {
    if (mutation.isPending) {
      return;
    }
    const input = {
      organizationBusinessUnitId: data.unit.id,
      expectedVersion: data.version,
      endDate: values.endDate,
    };
    onBusyChange(true);
    try {
      await mutation.mutateAsync(input);
      setOpen(false);
    } catch {
      // Stale submissions show the server's conflict and a reload action below.
    } finally {
      onBusyChange(false);
    }
  }

  return (
    <AlertDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!mutation.isPending) {
          setOpen(nextOpen);
        }
      }}
    >
      <AlertDialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="text-red-700"
          disabled={busy}
        >
          {hasScheduledEnd ? "Change end date" : "End plan"}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {hasScheduledEnd
              ? "Change the plan’s end date"
              : "End this billing plan?"}
          </AlertDialogTitle>
          <AlertDialogDescription>
            Select the last date of service in New York. The selected day is
            included. Unpaid invoices remain payable.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <form onSubmit={form.handleSubmit(endPlan)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="plan-end-date">End date (inclusive)</Label>
            <Input
              id="plan-end-date"
              type="date"
              required
              min={data.today}
              max={data.maximumEndDate ?? undefined}
              disabled={mutation.isPending}
              {...form.register("endDate", {
                onChange: () => mutation.reset(),
              })}
            />
            {endDate && (
              <p className="text-sm">
                Service continues through {formatPlanDate(endDate)}.
              </p>
            )}
            {data.maximumEndDate && (
              <p className="text-sm text-muted-foreground">
                A successor plan is scheduled. This plan can end no later than{" "}
                {formatPlanDate(data.maximumEndDate)}.
              </p>
            )}
            <p className="text-sm text-muted-foreground">
              You can change this date again before the plan ends.
            </p>
          </div>
          {mutation.error && (
            <div className="space-y-2">
              <p role="alert" className="text-sm text-red-700">
                {mutation.error.message}
              </p>
              {mutation.error.data?.code === "CONFLICT" && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onReload}
                  disabled={mutation.isPending}
                >
                  Reload latest plan
                </Button>
              )}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={mutation.isPending}>
              Keep plan
            </AlertDialogCancel>
            <Button
              variant="destructive"
              type="submit"
              disabled={mutation.isPending}
            >
              {mutation.isPending ? "Saving end date…" : "Confirm end date"}
            </Button>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
